import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import {DialogModule} from 'primeng/dialog';
import {
    SearchFilterComponent,
    FilterConfig
} from '@components/search-filter/search-filter.component';
import {TableModule} from 'primeng/table';
import {TooltipModule} from 'primeng/tooltip';
import {
    PlacementService,
    PlacementFeedingRecord,
    AddActivePlacementFeedingRecordRequest,
    UpdateActivePlacementFeedingRecordRequest,
    House
} from '@services/placement.service';
import {ToastrService} from 'ngx-toastr';
import {HasRoleDirective} from '@/has-role.directive';
import {PaginatorModule} from 'primeng/paginator';

interface HouseOption {
    id: number;
    name: string;
}
interface FeedingEntry {
    id?: number;
    dateTime: string; // ISO or local datetime string
    houseId: number;
    quantity: number;
}

@Component({
    selector: 'app-feeding-record',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        DialogModule,
        SearchFilterComponent,
        TableModule,
        TooltipModule,
        PaginatorModule,
        HasRoleDirective
    ],
    templateUrl: './feeding-record.component.html',
    styleUrl: './feeding-record.component.scss'
})
export class FeedingRecordComponent implements OnInit, OnChanges {
    name: string = '';
    pageSize: number = 12;
    totalRecords: number = 0;
    currentPage: number = 1;
    activeActionMenu: string | null = null;
    loading: boolean = false;
    data: any[] = [];

    private editingId: string | null = null;
    editingNumericId: number | null = null;
    // Deletion confirmation state
    showDeleteConfirm: boolean = false;
    private pendingDeleteId: string | null = null;

    onPageChange(event: any) {
        // PrimeNG paginator emits {page, first, rows}
        if (event && typeof event.page === 'number') {
            this.currentPage = event.page + 1; // convert to 1-based
        } else if (
            event &&
            typeof event.first === 'number' &&
            typeof event.rows === 'number'
        ) {
            this.currentPage = Math.floor(event.first / event.rows) + 1;
        }
        this.loadFeedingRecords();
    }

    toggleActionsMenu(id: string) {
        this.activeActionMenu = this.activeActionMenu === id ? null : id;
    }

    editFeeding(item: any) {
        const idx = this.findEntryIndexById(item?.id);
        if (idx === -1) return;
        const entry = this.filteredEntriesForEdit()[idx];
        // Patch form and open dialog
        this.form.patchValue({
            datetime: this.toLocalDatetimeInput(entry.dateTime),
            houseId: entry.houseId,
            quantity: entry.quantity
        });
        this.editingId = this.buildId(entry);
        this.editingNumericId = entry.id ?? null;
        this.showForm = true;
    }

    deleteFeeding(item: any) {
        // Open confirmation dialog
        this.pendingDeleteId = item?.id || null;
        if (!this.pendingDeleteId) {
            this.toastr.error('Record not found to delete');
            return;
        }
        this.showDeleteConfirm = true;
    }

    cancelDelete() {
        this.showDeleteConfirm = false;
        this.pendingDeleteId = null;
    }

    confirmDelete() {
        if (!this.pendingDeleteId) return;
        const idToken = this.pendingDeleteId;
        this.showDeleteConfirm = false;
        this.pendingDeleteId = null;
        this.performDeleteById(idToken);
    }

    private performDeleteById(idToken: string) {
        const idx = this.findEntryIndexById(idToken, true);
        if (idx === -1) {
            this.toastr.error('Record not found');
            return;
        }
        const entry = this.entries[idx];
        if (!entry?.id) {
            this.toastr.error('Record id missing, unable to delete');
            return;
        }

        this.loading = true;
        this.placementService
            .deleteActivePlacementFeedingRecord(entry.id)
            .subscribe({
                next: () => {
                    this.toastr.success('Feeding record deleted');
                    this.loadFeedingRecords();
                },
                error: () => {
                    this.loading = false;
                    this.toastr.error('Failed to delete feeding record');
                }
            });
    }
    // Optional input from parent: list of houses in the cycle
    @Input() houses: HouseOption[] | null = null;
    @Input() placementId: number | null = null;

    defaultHouses: HouseOption[] = [];

    entries: FeedingEntry[] = [];

    // Filters and record form
    filterForm: FormGroup;
    filterConfig: FilterConfig = {
        fields: [
            {key: 'start', label: 'Start date', type: 'date'},
            {key: 'end', label: 'End date', type: 'date'},
            {
                key: 'houseId',
                label: 'House',
                type: 'select',
                options: []
            }
        ],
        showFarmFilter: false,
        showHouseFilter: false,
        showPartitionFilter: false
    };
    form: FormGroup;
    showForm = false;

    constructor(
        private fb: FormBuilder,
        private placementService: PlacementService,
        private toastr: ToastrService
    ) {
        this.form = this.fb.group({
            datetime: ['', Validators.required],
            houseId: [null, Validators.required],
            quantity: [null, [Validators.required, Validators.min(0.01)]]
        });

        this.filterForm = this.fb.group({
            start: [null],
            end: [null],
            houseId: [null]
        });
    }

    get houseOptions(): HouseOption[] {
        return this.houses && this.houses.length
            ? this.houses
            : this.defaultHouses;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['houses']) {
            this.updateFilterOptions();
        }
        if (changes['placementId'] && this.placementId) {
            this.loadHouses();
            this.loadFeedingRecords();
        } else {
            this.refreshTable();
        }
    }

    ngOnInit(): void {
        this.updateFilterOptions();
        if (this.placementId) {
            this.loadHouses();
            this.loadFeedingRecords();
        } else {
            this.refreshTable();
        }
    }

    private updateFilterOptions(): void {
        this.filterConfig = {
            ...this.filterConfig,
            fields: this.filterConfig.fields.map((f) =>
                f.key === 'houseId'
                    ? {
                          ...f,
                          options: this.houseOptions.map((h) => ({
                              value: h.id,
                              label: h.name
                          }))
                      }
                    : f
            )
        };
    }

    openForm() {
        const now = new Date();
        const localDateTime = this.toLocalDatetimeInput(now.toISOString());

        this.form.patchValue({
            datetime: localDateTime
        });

        this.showForm = true;
    }
    closeForm() {
        this.showForm = false;
        this.form.reset();
        this.editingId = null;
        this.editingNumericId = null;
    }

    submit() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        if (!this.placementId) {
            this.toastr.error('Placement not found');
            return;
        }
        const value = this.form.value;
        const dateTimeString: string = this.toIsoStringFromLocalInput(
            value.datetime
        );
        const userDetailStr = localStorage.getItem('UserDetail');
        const userDetail = userDetailStr ? JSON.parse(userDetailStr) : null;
        const createdBy = userDetail?.id ?? 0;

        if (this.editingId && this.editingNumericId) {
            const payload: UpdateActivePlacementFeedingRecordRequest = {
                id: this.editingNumericId,
                cycleId: this.placementId,
                farmHouseId: Number(value.houseId),
                quantity: Number(value.quantity),
                dateTime: dateTimeString,
                createdBy
            };
            this.loading = true;
            this.placementService
                .updateActivePlacementFeedingRecord(payload)
                .subscribe({
                    next: () => {
                        this.toastr.success('Feeding record updated');
                        this.closeForm();
                        this.loadFeedingRecords();
                    },
                    error: () => {
                        this.loading = false;
                        this.toastr.error('Failed to update record');
                    }
                });
        } else {
            const payload: AddActivePlacementFeedingRecordRequest = {
                cycleId: this.placementId,
                farmHouseId: Number(value.houseId),
                quantity: Number(value.quantity),
                dateTime: dateTimeString,
                createdBy
            } as AddActivePlacementFeedingRecordRequest;
            this.loading = true;
            this.placementService
                .addActivePlacementFeedingRecord(payload)
                .subscribe({
                    next: () => {
                        this.toastr.success('Feeding record added');
                        this.closeForm();
                        this.loadFeedingRecords();
                    },
                    error: () => {
                        this.loading = false;
                        this.toastr.error('Failed to add record');
                    }
                });
        }
    }

    get filteredEntries(): FeedingEntry[] {
        const {houseId, start, end} = this.filterForm.value;
        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;

        // Set end date to end of day for inclusive filtering
        if (endDate) {
            endDate.setHours(23, 59, 59, 999);
        }

        return this.entries.filter((e) => {
            // House filter
            if (houseId && Number(houseId) !== e.houseId) return false;

            // Date filter
            const dt = new Date(e.dateTime);
            if (startDate && dt < startDate) return false;
            if (endDate && dt > endDate) return false;

            return true;
        });
    }

    get uniqueDatetimes(): string[] {
        const set = new Set(this.filteredEntries.map((e) => e.dateTime));
        return Array.from(set).sort(
            (a, b) => new Date(a).getTime() - new Date(b).getTime()
        );
    }

    quantityFor(datetime: string, houseId: number): number | null {
        const found = this.filteredEntries.find(
            (e) => e.dateTime === datetime && e.houseId === houseId
        );
        return found ? found.quantity : null;
    }

    onFilter(values: any) {
        // Update the filter form with the new filter values
        if (values) {
            this.filterForm.patchValue(values);
        }
        this.currentPage = 1; // Reset to first page when filtering
        this.loadFeedingRecords();
    }

    onFilterCleared() {
        // Reset filter form to defaults
        this.filterForm.patchValue({
            start: null,
            end: null,
            houseId: null
        });
        this.currentPage = 1; // Reset to first page when clearing filters
        this.loadFeedingRecords();
    }

    // ----- Helpers for smart table -----
    private refreshTable(): void {
        this.data = this.entries.map((e) => this.toTableItem(e));
    }

    private toTableItem(e: FeedingEntry): any {
        const house =
            this.houseOptions.find((h) => h.id === e.houseId)?.name ||
            'Unknown';
        const id = this.buildId(e);
        return {
            id,
            time: this.formatLocalDateTime(e.dateTime),
            name: house,
            quantity: e.quantity,
            phone: null,
            createdOn: new Date(e.dateTime),
            email: null
        };
    }

    private buildId(e: FeedingEntry): string {
        return e.id != null ? `id:${e.id}` : `${e.dateTime}|${e.houseId}`;
    }

    private findEntryIndexById(
        id: string | null | undefined,
        onFullList: boolean = false
    ): number {
        if (!id) return -1;
        // Find in either the filtered list (for edit selection) or full entries (for destructive ops)
        const list = onFullList ? this.entries : this.filteredEntriesForEdit();
        return list.findIndex((e) => this.buildId(e) === id);
    }

    private filteredEntriesForEdit(): FeedingEntry[] {
        // Keep the same ordering as filteredEntries getter
        return this.filteredEntries;
    }

    // -------- Service wiring --------
    private loadHouses(): void {
        if (!this.placementId) return;
        this.placementService.getHousesByCycleId(this.placementId).subscribe({
            next: (houses: House[]) => {
                const mapped: HouseOption[] = houses.map((h) => ({
                    id: h.houseId,
                    name: h.houseName
                }));
                this.houses = mapped;
                this.updateFilterOptions();
            },
            error: () => {
                // keep defaults
            }
        });
    }

    private loadFeedingRecords(): void {
        if (!this.placementId) return;
        this.loading = true;
        const fv = this.filterForm?.value || {};
        const farmHouseId = fv.houseId ? Number(fv.houseId) : 0;
        const fromDate = fv.start ? String(fv.start) : undefined;
        const toDate = fv.end ?? undefined;

        this.placementService
            .getAllActivePlacementFeedingRecordsByCycleId(
                this.placementId,
                this.currentPage,
                this.pageSize,
                {
                    farmHouseId,
                    fromDate,
                    toDate
                }
            )
            .subscribe({
                next: (response) => {
                    const records = response?.items || [];
                    this.entries = records.map((r) => ({
                        id: r.id,
                        dateTime: r.dateTime,
                        houseId: r.farmHouseId,
                        quantity: r.quantity
                    }));
                    this.totalRecords = response?.meta?.totalCount || 0;
                    this.loading = false;
                    this.refreshTable();
                },
                error: () => {
                    this.loading = false;
                    this.toastr.error('Failed to load feeding records');
                }
            });
    }

    private toLocalDatetimeInput(dateTime: string): string {
        // Convert ISO or parseable string to input[type=datetime-local] format (local timezone)
        const d = new Date(dateTime);
        const tzOffsetMs = d.getTimezoneOffset() * 60000;
        const local = new Date(d.getTime() - tzOffsetMs)
            .toISOString()
            .slice(0, 16);
        return local;
    }

    private toIsoStringFromLocalInput(localInput: string): string {
        // localInput: 'YYYY-MM-DDTHH:mm' in local time. Convert to ISO string.
        // Create Date as if local time, then convert to real ISO
        const d = new Date(localInput);
        return d.toISOString();
    }

    private formatLocalDateTime(dateTime: string): string {
        // Format dateTime as local string: 'YYYY-MM-DD HH:mm'
        const d = new Date(dateTime);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }
}
